import { useState } from "react";
import { ArrowUpRight, Check, MapPin, MessageCircle, Phone } from "lucide-react";
import axios from "axios";
import { MaskedLine, FadeUp, Overline } from "../components/Reveal";
import {
  ADDRESS_LINES,
  MAPS_LINK,
  PHONE_1,
  PHONE_2,
  PHONE_1_LINK,
  PHONE_2_LINK,
  WHATSAPP_LINK,
} from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-ink/25 px-4 py-3 text-sm placeholder:text-ink/35 focus:outline-none focus:border-ink transition-colors";

const emptyForm = {
  name: "",
  company: "",
  phone: "",
  email: "",
  product: "",
  quantity: "",
  requirement: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle");
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        requirement: form.requirement.trim(),
      };
      if (form.company.trim()) payload.company = form.company.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.product.trim()) payload.product = form.product.trim();
      if (form.quantity.trim()) payload.quantity = form.quantity.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      await axios.post(`${API}/enquiries`, payload);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="grid grid-cols-1 lg:grid-cols-2 border-b border-ink/15 min-h-screen">
        <div className="pt-32 sm:pt-44 pb-16 px-5 sm:px-8 lg:px-12 lg:border-r border-ink/15 flex flex-col">
          <FadeUp immediate y={12}>
            <Overline className="mb-8">Contact</Overline>
          </FadeUp>
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-[12vw] sm:text-[11vw] lg:text-[4.5vw]">
            <MaskedLine immediate delay={0.15}>Let&apos;s Talk</MaskedLine>
            <MaskedLine immediate delay={0.3}>Hardware.</MaskedLine>
          </h1>

          <div className="mt-14 space-y-10">
            <FadeUp delay={0.2}>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/45 mb-3">Call Us</p>
              <div className="space-y-1">
                <a data-testid="contact-phone-1" href={PHONE_1_LINK} className="block font-display font-bold tracking-tight text-2xl sm:text-3xl link-underline w-fit">
                  {PHONE_1}
                </a>
                <a data-testid="contact-phone-2" href={PHONE_2_LINK} className="block font-display font-bold tracking-tight text-2xl sm:text-3xl link-underline w-fit">
                  {PHONE_2}
                </a>
              </div>
            </FadeUp>

            <FadeUp delay={0.3}>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-ink/45 mb-3">Visit Us</p>
              <address className="not-italic text-base text-ink/75 leading-relaxed">
                {ADDRESS_LINES.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
            </FadeUp>

            <FadeUp delay={0.4} className="flex flex-wrap gap-3">
              <a
                data-testid="contact-call-button"
                href={PHONE_1_LINK}
                className="inline-flex items-center gap-2 bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 hover:bg-accent hover:text-ink transition-colors"
              >
                <Phone size={14} /> Call Us
              </a>
              <a
                data-testid="contact-whatsapp-button"
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-ink/30 font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 hover:border-ink transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                data-testid="contact-directions-button"
                href={MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-ink/30 font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 hover:border-ink transition-colors"
              >
                <MapPin size={14} /> Get Directions
              </a>
            </FadeUp>
          </div>
        </div>

        <div className="bg-white px-5 sm:px-8 lg:px-12 pt-16 lg:pt-44 pb-16 border-t lg:border-t-0 border-ink/15">
          {status === "success" ? (
            <div data-testid="contact-success-message" className="h-full flex flex-col justify-center py-16">
              <span className="inline-flex h-14 w-14 items-center justify-center bg-accent text-ink mb-8">
                <Check size={26} strokeWidth={2} />
              </span>
              <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-5xl leading-none mb-6">
                Thank you.
              </h2>
              <p className="text-ink/70 leading-relaxed max-w-md">
                Your requirement has been received. The Jagdamba Enterprises team will get in
                touch with you.
              </p>
              <button
                data-testid="contact-send-another-button"
                onClick={() => {
                  setForm(emptyForm);
                  setStatus("idle");
                }}
                className="mt-10 self-start bg-ink text-paper font-mono text-[11px] tracking-[0.2em] uppercase px-7 py-4 hover:bg-accent hover:text-ink transition-colors"
              >
                Send Another Requirement
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="max-w-xl lg:pt-2">
              <Overline className="mb-3">Enquiry Form</Overline>
              <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-none mb-10">
                Send Requirement
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Phone Number *</label>
                  <input id="contact-phone" data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91" />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Company Name</label>
                  <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Email</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label htmlFor="contact-product" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Product</label>
                  <input id="contact-product" data-testid="contact-product-input" value={form.product} onChange={set("product")} className={inputCls} placeholder="e.g. Hex Bolt" />
                </div>
                <div>
                  <label htmlFor="contact-quantity" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Quantity</label>
                  <input id="contact-quantity" data-testid="contact-quantity-input" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500 pcs" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-requirement" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Requirement *</label>
                  <textarea id="contact-requirement" data-testid="contact-requirement-input" required rows={3} value={form.requirement} onChange={set("requirement")} className={`${inputCls} resize-none`} placeholder="What do you need? Sizes, material, brands…" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">Message</label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Anything else we should know?" />
                </div>
              </div>

              {status === "error" && (
                <p data-testid="contact-error-message" className="mt-5 text-sm text-red-700">
                  Something went wrong. Please try again, or call us directly.
                </p>
              )}

              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={status === "sending"}
                className="mt-8 w-full bg-ink text-paper font-mono text-xs tracking-[0.2em] uppercase px-6 py-4 flex items-center justify-center gap-2 hover:bg-accent hover:text-ink transition-colors disabled:opacity-60"
              >
                {status === "sending" ? "Sending…" : "Send Requirement"}
                <ArrowUpRight size={14} />
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
